"use client";

import { Plus } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Expertisefaq() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const faqs = [{
    question: "How do you improve OTA revenue?",
    answer: "We optimize your OTA presence by refining rate parity, enhancing content quality, and leveraging data-driven pricing strategies to increase visibility and conversion rates across all major booking platforms."
  }, {
    question: "What tech stacks do you recommend?",
    answer: "We evaluate your current infrastructure and recommend integrated PMS, Channel Manager, and RMS solutions that streamline operations and provide actionable insights for your specific property type."
  }, {
    question: "How does a revenue audit work?",
    answer: "Our audit involves a deep dive into your historical data, competitor benchmarking, and current distribution channels to identify missed opportunities and implement corrective revenue strategies."
  }, {
    question: "Can you help with direct bookings?",
    answer: "Yes. We implement digital marketing strategies and website conversion tactics designed to shift traffic from high-commission OTAs to your direct booking engine, increasing your net profit margins."
  }, {
    question: "What is your consulting process?",
    answer: "We begin with a discovery call to understand your goals, followed by a comprehensive property analysis, strategy development, and ongoing performance monitoring to ensure sustained revenue growth."
  }, {
    question: "Do you work with small hotels?",
    answer: "We partner with independent hotels, boutique properties, and investors. Our strategies are scalable and tailored to the unique needs and market position of each individual property."
  }, {
    question: "How do we measure success?",
    answer: "Success is measured through key performance indicators including RevPAR, GOPPAR, ADR, and occupancy rates, tracked against your historical performance and competitive market benchmarks."
  }, {
    question: "How do I start a consultation?",
    answer: "Simply fill out our contact form or book a discovery session directly through our website. We will review your property details and reach out to schedule an initial assessment."
  }];
  return <section data-section-id="1442" id="expertisefaq" className="relative bg-background text-foreground py-28 md:py-40 overflow-hidden">
      {}
      <div className="absolute top-0 left-0 right-0 h-[2px] bg-secondary" />

      <div ref={ref} className={`mx-auto max-w-[1320px] px-6 md:px-10 lg:px-16 transition-all duration-700 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-28 relative">
              {}
              <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-20 -z-0">
                <div className="absolute top-12 left-0 right-0 bottom-0 border-2 border-primary rounded-md">
                  <div className="flex border-b border-primary/60 px-3 py-2 gap-4">
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">
                      ID
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary">
                      Query
                    </span>
                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-primary ml-auto">
                      Status
                    </span>
                  </div>
                  {[...Array(10)].map((_, i) => <div key={i} data-index={i} className="h-7 border-b border-primary/40" />)}
                </div>
              </div>

              <div className="relative z-10">
                {}
                <div className="inline-block mb-10 -rotate-[8deg]">
                  <div className="border-2 border-double border-secondary rounded-sm px-4 py-2 bg-background/40">
                    <div className="font-mono text-[11px] font-medium tracking-[0.22em] uppercase text-secondary">
                      Expert · Verified
                    </div>
                  </div>
                </div>

                {}
                <div className="mb-6 flex items-center gap-2">
                  <span className="font-mono text-xs font-medium tracking-[0.22em] uppercase text-secondary">
                    §
                  </span>
                  <span className="font-mono text-xs font-medium tracking-[0.22em] uppercase text-primary">
                    No. 03 — Insights
                  </span>
                </div>

                {}
                <h2 className="font-serif font-normal text-primary text-[clamp(2.25rem,4.4vw,4.25rem)] leading-[1.02] tracking-tight mb-8">
                  Revenue &amp;{" "}
                  <span className="relative italic inline-block">
                    Strategy
                    <span aria-hidden="true" className="absolute left-0 right-0 -bottom-1.5 h-[2px] bg-secondary" />
                  </span>
                </h2>

                {}
                <p className="font-default text-foreground/85 text-[clamp(1.0625rem,1.15vw,1.25rem)] leading-[1.6]">
                  Answers to common questions regarding hotel revenue management, 
                  OTA optimization, and technology implementation. These insights 
                  are designed to help property owners and managers make informed 
                  decisions to maximize profitability.
                </p>

                {}
                <div className="relative mt-10 flex items-center">
                  <span className="size-1.5 rounded-lg bg-secondary shrink-0" />
                  <div className="flex-1 border-t border-dashed border-primary" />
                  <span className="size-1.5 rounded-lg bg-secondary shrink-0" />
                </div>

                {}
                <div className="mt-6 font-mono text-[11px] tracking-[0.14em] text-foreground/60">
                  Revenue · Strategy · Growth
                </div>
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible className="w-full border-t border-primary">
              {faqs.map((faq, i) => {
              const index = String(i + 1).padStart(2, "0");
              return <AccordionItem key={i} data-index={i} value={`item-${i}`} className="border-b-0 group">
                    <AccordionTrigger className="hover:no-underline py-7 [&>svg]:hidden">
                      <div className="flex items-start gap-5 md:gap-8 w-full">
                        <span className="font-mono text-xs md:text-sm font-medium tracking-[0.18em] uppercase text-primary pt-1 shrink-0 w-8">
                          {index}.
                        </span>
                        <span className="font-serif font-semibold text-primary text-[clamp(1.125rem,1.4vw,1.375rem)] leading-[1.3] flex-1 text-left">
                          {faq.question}
                        </span>
                        <span className="shrink-0 pt-1">
                          <Plus className="size-5 text-secondary transition-transform duration-[380ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-data-[state=open]:rotate-45" strokeWidth={1.5} />
                        </span>
                      </div>
                    </AccordionTrigger>

                    {}
                    <div className="relative flex items-center pl-13 md:pl-16 pr-10">
                      <span className="size-1.5 rounded-lg bg-secondary shrink-0" />
                      <div className="flex-1 border-t border-dashed border-primary/70" />
                      <span className="size-1.5 rounded-lg bg-secondary shrink-0" />
                    </div>

                    <AccordionContent className="pt-6 pb-8">
                      <div className="flex gap-5 md:gap-8">
                        <span className="w-8 shrink-0" aria-hidden="true" />
                        <p className="font-default text-foreground/85 text-base md:text-lg leading-[1.65] flex-1 pr-10">
                          {faq.answer}
                        </p>
                      </div>
                    </AccordionContent>
                  </AccordionItem>;
            })}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}